import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { http } from "../lib/api";
import { useSeo } from "../hooks/useSeo";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Bits";
import { RESOURCE_CATEGORIES } from "../lib/site";
import { IMAGES } from "../lib/images";

export default function Resources() {
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState("All");

  useSeo({
    title: "Combat Mat Guides and Gym Design Resources | MAKO Mats",
    description:
      "Practical guides on choosing combat mats, gym design, mat care, installation and sport-specific surface advice from MAKO Mats.",
  });

  useEffect(() => {
    http.get("/content/articles").then((r) => setArticles(r.data)).catch(() => {});
  }, []);

  const filtered =
    category === "All" ? articles : articles.filter((a) => a.category === category);

  return (
    <div data-testid="resources-page">
      <PageHero
        eyebrow="Resources"
        title="Knowledge for"
        subtitle="better training spaces"
        copy="Practical guidance on choosing, planning, installing and maintaining professional combat surfaces."
        image={IMAGES.floor_plan}
      />

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex flex-wrap gap-2.5" data-testid="resource-filters">
            {["All", ...RESOURCE_CATEGORIES].map((c) => (
              <button
                key={c}
                data-testid={`resource-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`border px-4 py-2.5 text-[0.6875rem] font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
                  category === c
                    ? "border-[#0F4C81] bg-[#0F4C81] text-white"
                    : "border-[#111111]/15 text-[#3B3B3B] hover:border-[#0F4C81] hover:text-[#0F4C81]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-3" data-testid="article-grid">
            {filtered.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 3) * 0.07}>
                <Link
                  to={`/resources/${a.slug}`}
                  data-testid={`article-card-${a.slug}`}
                  className="group block"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-[#F5F5F2]">
                    <img
                      src={a.image}
                      alt={a.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
                    />
                  </div>
                  <p className="mako-label mt-6 text-[#0F4C81]">{a.category}</p>
                  <h2 className="mt-4 font-display text-lg font-bold leading-snug text-[#111111] transition-colors duration-300 group-hover:text-[#0F4C81]">
                    {a.title}
                  </h2>
                  <p className="mt-4 text-sm leading-relaxed text-[#3B3B3B]">{a.excerpt}</p>
                  <span className="mako-label mt-6 inline-flex items-center gap-2 text-[#7A7F85]">
                    {a.read_time}
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="mt-14 text-sm text-[#7A7F85]" data-testid="resources-empty">
              No articles in this category yet.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
